import { useState } from 'react'
import { useQuery } from '../api/hooks'
import { api, query } from '../api/client'
import type { AccountNode } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import { ErrorBanner, Field, Loading, Money, Modal, Panel, Tag, isoDate } from '../components/ui'

/**
 * The chart of accounts as a tree, with slash-delimited codes (BUILD_PLAN §3.4).
 * Only leaves are postable; parents roll their children up.
 */
export function ChartOfAccountsPage() {
  const { can } = useAuth()
  const [includeInactive, setIncludeInactive] = useState(false)
  const [withBalances, setWithBalances] = useState(false)
  const [asOf, setAsOf] = useState(isoDate(new Date()))
  const [creating, setCreating] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const tree = useQuery<AccountNode[]>(
    `/accounts/tree${query({ includeInactive, balanceAsOf: withBalances ? asOf : '' })}`,
    [includeInactive, withBalances, asOf],
  )

  function toggle(id: string) {
    setCollapsed((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <label style={{ textTransform: 'none', fontWeight: 400, fontSize: 13 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 6 }}
            checked={withBalances}
            onChange={(e) => setWithBalances(e.target.checked)}
          />
          Show balances
        </label>

        {withBalances && (
          <div className="field">
            <label>As at</label>
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </div>
        )}

        <label style={{ textTransform: 'none', fontWeight: 400, fontSize: 13 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 6 }}
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Include inactive
        </label>

        <div className="spacer" />

        {can(P.accounts.create) && (
          <button className="primary" onClick={() => setCreating(true)}>
            + New account
          </button>
        )}
      </div>

      <ErrorBanner error={tree.error} />
      {tree.loading && <Loading what="Loading the chart" />}

      {tree.data && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Code</th>
                  <th>Account</th>
                  <th style={{ width: 200 }}>العربية</th>
                  <th style={{ width: 110 }}>Type</th>
                  <th style={{ width: 150 }}>Rules</th>
                  {withBalances && (
                    <th style={{ width: 150 }} className="num">
                      Balance
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {tree.data.map((root) => (
                  <AccountRows
                    key={root.id}
                    node={root}
                    depth={0}
                    collapsed={collapsed}
                    onToggle={toggle}
                    withBalances={withBalances}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {creating && (
        <NewAccount
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            tree.reload()
          }}
        />
      )}
    </>
  )
}

function AccountRows({
  node,
  depth,
  collapsed,
  onToggle,
  withBalances,
}: {
  node: AccountNode
  depth: number
  collapsed: Set<string>
  onToggle: (id: string) => void
  withBalances: boolean
}) {
  const hasChildren = node.children.length > 0
  const isCollapsed = collapsed.has(node.id)

  return (
    <>
      <tr style={{ opacity: node.isActive ? 1 : 0.5 }}>
        <td className="code" style={{ paddingLeft: 10 + depth * 16 }}>
          <span className="tree-toggle" onClick={() => hasChildren && onToggle(node.id)}>
            {hasChildren ? (isCollapsed ? '▸' : '▾') : ''}
          </span>
          {node.code}
        </td>
        <td style={{ fontWeight: hasChildren ? 600 : 400 }}>{node.nameEn}</td>
        <td dir="rtl" className="soft">
          {node.nameAr}
        </td>
        <td>
          <span className="muted">{node.type}</span>
        </td>
        <td>
          {/* The rules that make the client-money separation structural rather than a convention. */}
          {node.natureRequired && <Tag kind="warn">nature</Tag>}{' '}
          {node.partyRequired && <Tag kind="info">party</Tag>}{' '}
          {node.isLeaf && !node.allowManualEntry && <Tag>sub-ledger</Tag>}{' '}
          {node.currencyRestriction && <Tag>{node.currencyRestriction}</Tag>}
        </td>
        {withBalances && (
          <td className="num">
            <Money value={node.balance} blankZero />
          </td>
        )}
      </tr>

      {!isCollapsed &&
        node.children.map((child) => (
          <AccountRows
            key={child.id}
            node={child}
            depth={depth + 1}
            collapsed={collapsed}
            onToggle={onToggle}
            withBalances={withBalances}
          />
        ))}
    </>
  )
}

function NewAccount({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [nameAr, setNameAr] = useState('')
  const [currencyRestriction, setCurrency] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      await api('/accounts', {
        method: 'POST',
        body: {
          code: code.trim(),
          nameEn: nameEn.trim(),
          nameAr: nameAr.trim(),
          currencyRestriction: currencyRestriction || null,
          allowManualEntry: true,
        },
      })
      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="New account"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !code || !nameEn} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Create'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <Field
        label="Code"
        hint="Slash-delimited, e.g. 5/2/15. The parent must already exist, and a parent that already carries postings cannot be given children."
      >
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="5/2/15" />
      </Field>

      <Field label="Name (English)">
        <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
      </Field>

      <Field label="Name (Arabic)">
        <input dir="rtl" value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
      </Field>

      <Field label="Currency restriction" hint="Leave blank unless the account holds one currency only.">
        <select value={currencyRestriction} onChange={(e) => setCurrency(e.target.value)}>
          <option value="">Any currency</option>
          <option value="EGP">EGP</option>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </select>
      </Field>
    </Modal>
  )
}
